// Step 23 — contract status card.
//
// Renders the deterministic lifecycle state of a ContractDraft using
// only fields the backend already exposes. Explicitly framed so a
// reader can never mistake "the draft exists" for "the draft is
// approved / signed / sent".
//
// No approve / reject / send / sign buttons — this is the intelligence
// workspace. The human-controlled lifecycle lives on the existing
// contracts review surface (ContractsPanel / ContractDetail).

import { AISection } from "../AIPrimitives";
import { contractStatusLabel, EDITABLE_STATUSES, TERMINAL_STATUSES } from "./helpers";

function _fmtWhen(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
}

export default function AIContractStatusCard({ detail }) {
  if (!detail) return null;

  const status = detail.status;
  const editable = EDITABLE_STATUSES.has(status);
  const terminal = TERMINAL_STATUSES.has(status);

  const timeline = [];
  if (detail.created_at) {
    timeline.push(["Skapad", _fmtWhen(detail.created_at)]);
  }
  if (detail.updated_at) {
    timeline.push(["Uppdaterad", _fmtWhen(detail.updated_at)]);
  }
  if (detail.approved_at) {
    timeline.push(["Godkänd", _fmtWhen(detail.approved_at)]);
  }
  if (detail.sent_for_signature_at) {
    timeline.push([
      "Skickad för signering",
      _fmtWhen(detail.sent_for_signature_at),
    ]);
  }
  if (detail.signed_at) {
    timeline.push(["Signerad", _fmtWhen(detail.signed_at)]);
  }
  if (detail.rejected_at) {
    timeline.push(["Avvisad", _fmtWhen(detail.rejected_at)]);
  }

  return (
    <AISection title="Status">
      <div className="qai-contract-status">
        <div className="qai-contract-status-badge-row">
          <span
            className="qai-contract-status-badge"
            data-status={status}
          >
            {contractStatusLabel(status)}
          </span>
          {editable && (
            <span
              className="qai-contract-status-hint"
              data-tone="editable"
            >
              Redigerbar — kroppen och variabler kan fortfarande ändras.
            </span>
          )}
          {terminal && (
            <span
              className="qai-contract-status-hint"
              data-tone="terminal"
            >
              Terminal — inga fler övergångar möjliga.
            </span>
          )}
        </div>

        {timeline.length > 0 && (
          <dl className="qai-contract-status-timeline">
            {timeline.map(([k, v]) => (
              <div key={k} className="qai-contract-status-row">
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="qai-contract-status-boundary">
          AI-intelligensvyn kan INTE godkänna, avvisa, skicka för
          signering, signera, avbryta eller ersätta detta utkast. Alla
          sådana åtgärder sker manuellt i granskningsvyn.
        </div>
      </div>
    </AISection>
  );
}
